/**
 * <s-subcollection-filter> — sub-collection tiles that filter the collection grid in place.
 *
 * Each tile is a button[data-subcollection-tile] with `data-url` (the collection to show)
 * and `data-partials` (partial names that collection's template renders). Pressing a tile
 * (command `--select`) highlights it, repoints the target <s-live-form>'s form action at
 * that collection, and refreshes the grid without leaving the page. Pressing the active
 * tile again, or the "All" tile, returns to the parent collection.
 *
 * Attributes:
 *   target — id of the <s-live-form> that owns the product grid.
 */
class SSubcollectionFilter extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener(
      'command',
      (event) => {
        if (event.command !== '--select' || !(event.source instanceof HTMLElement)) return;
        this.#select(event.source);
      },
      { signal: this.#ac.signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #tiles() {
    return [...this.querySelectorAll('[data-subcollection-tile]')];
  }

  #select(button) {
    const allTile = this.querySelector('[data-subcollection-all]');
    const wasPressed = button.getAttribute('aria-pressed') === 'true';
    const chosen = wasPressed && button !== allTile ? allTile : button;
    if (!chosen) return;

    for (const tile of this.#tiles) {
      tile.setAttribute('aria-pressed', tile === chosen ? 'true' : 'false');
    }
    this.toggleAttribute('data-has-selection', chosen !== allTile);

    const liveForm = document.getElementById(this.getAttribute('target') || '');
    const form = liveForm?.querySelector('form');
    if (!liveForm || !form || typeof liveForm.refresh !== 'function') {
      window.location.href = chosen.dataset.url;
      return;
    }

    // The Filter button is hidden while "All" is active, so drop any filter picks made on a sub-collection.
    if (chosen === allTile) {
      for (const input of form.querySelectorAll('input[name^="filter."]')) {
        if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
        else input.value = '';
      }
    }

    form.action = chosen.dataset.url;
    if (chosen.dataset.partials) liveForm.setAttribute('partial', chosen.dataset.partials);
    liveForm.refresh();
  }
}

customElements.define('s-subcollection-filter', SSubcollectionFilter);
